<section
  class="workflow-decision-editor"
  [class.workflow-decision-editor--contextual]="contextual"
  [class.workflow-decision-editor--status-checks]="statusChecksPresentation"
  aria-labelledby="workflow-decision-editor-title"
  [attr.aria-busy]="referenceLoading ? 'true' : null"
>
  <header class="workflow-decision-editor__header">
    <div>
      <h4
        #decisionSectionHeading
        id="workflow-decision-editor-title"
        tabindex="-1"
      >
        {{
          statusChecksPresentation
            ? "Decision checks for status changes"
            : "Recorded decision requirements"
        }}
      </h4>
      <p
        *ngIf="statusChecksPresentation && statusCheckContextLabel"
        class="workflow-decision-editor__status-context"
        data-testid="status-check-context"
      >
        Editing checks for <strong>{{ statusCheckContextLabel }}</strong>
      </p>
      <p *ngIf="statusChecksPresentation">
        Check an already recorded decision before a status change can proceed.
        This does not collect a new review. To collect one in this step, add a
        review under Completion requirements.
      </p>
      <p *ngIf="!contextual && !statusChecksPresentation">
        {{
          inspectionMode
            ? "Inspect the recorded decisions required by this immutable workflow version."
            : "Define which recorded decisions are required and what happens when a status change is requested."
        }}
      </p>
    </div>
  </header>

  <p
    *ngIf="!inspectionMode && referenceLoading"
    class="workflow-decision-editor__state"
    role="status"
    aria-live="polite"
  >
    Loading active decision, status, role, and queue choices. Existing rules
    remain available to inspect.
  </p>

  <div
    *ngIf="!inspectionMode && referenceError as error"
    class="workflow-decision-editor__error"
    role="alert"
    aria-live="assertive"
  >
    <strong>{{ error.title }}</strong>
    <ul>
      <li *ngFor="let message of error.messages">{{ message }}</li>
    </ul>
    <p *ngIf="referenceDataReady">
      The last successfully loaded authoring choices remain available.
    </p>
    <button
      uiButton
      [text]="true"
      [outlined]="false"
      type="button"
      class="workflow-decision-editor__text-button"
      [disabled]="referenceLoading"
      (click)="retryReferencesRequested.emit()"
    >
      Retry authoring choices
    </button>
  </div>

  <p
    *ngIf="
      !inspectionMode &&
      !referenceLoading &&
      !referenceDataReady &&
      !referenceError
    "
    class="workflow-decision-editor__state"
    role="status"
  >
    No active decision choices are available. Existing rules can be inspected,
    but authoring is disabled.
  </p>

  <p
    *ngIf="statusMessage() as message"
    class="workflow-decision-editor__notice"
    role="status"
    aria-live="polite"
  >
    {{ message }}
  </p>

  <div
    *ngIf="localError() as error"
    #decisionError
    class="workflow-decision-editor__error"
    role="alert"
    aria-live="assertive"
    tabindex="-1"
  >
    {{ error }}
  </div>

  <div class="workflow-decision-editor__workspace">
    <aside
      class="workflow-decision-editor__scopes"
      [hidden]="contextual"
      aria-labelledby="workflow-decision-scopes-title"
      data-testid="decision-scope-rail"
    >
      <header class="workflow-decision-editor__column-header">
        <h5 id="workflow-decision-scopes-title">Where rules apply</h5>
      </header>

      <nav aria-label="Decision rule scopes">
        <ng-container
          *ngFor="
            let group of scopeGroups;
            let groupIndex = index;
            trackBy: trackScopeGroup
          "
        >
          <p
            *ngIf="group.kind === 'step' && groupIndex === 1"
            class="workflow-decision-editor__scope-kicker"
          >
            Work steps
          </p>
          <p
            *ngIf="group.kind === 'evidence'"
            class="workflow-decision-editor__scope-kicker"
          >
            Preserved evidence
          </p>
          <button
            [wrap]="true"
            uiButton
            [text]="true"
            [outlined]="false"
            type="button"
            class="workflow-decision-editor__scope-row"
            [class.workflow-decision-editor__scope-row--selected]="
              group.key === selectedScopeKey()
            "
            [attr.aria-current]="
              group.key === selectedScopeKey() ? 'page' : null
            "
            [disabled]="interactionBlocked || notificationEditorPending"
            (click)="selectScope(group, $event.detail === 0)"
          >
            <span class="workflow-decision-editor__scope-name">{{
              group.label
            }}</span>
            <span class="workflow-decision-editor__scope-count">
              {{ group.structuredCount }} rule{{
                group.structuredCount === 1 ? "" : "s"
              }}
              <ng-container *ngIf="group.legacyCount > 0">
                · {{ group.legacyCount }} legacy
              </ng-container>
            </span>
          </button>
        </ng-container>
      </nav>
    </aside>

    <section
      class="workflow-decision-editor__navigator"
      aria-labelledby="workflow-decision-list-title"
      data-testid="decision-rule-navigator"
    >
      <header class="workflow-decision-editor__column-header">
        <div>
          <h5
            #decisionRuleListHeading
            id="workflow-decision-list-title"
            tabindex="-1"
          >
            <ng-container *ngIf="contextual; else fullDecisionListTitle">
              {{
                statusChecksPresentation
                  ? "Decision checks"
                  : "Recorded decisions"
              }}
              <span
                class="workflow-decision-editor__title-count"
                aria-live="polite"
              >
                ({{ visibleRuleRows.length }})
              </span>
            </ng-container>
            <ng-template #fullDecisionListTitle>Decision rules</ng-template>
          </h5>
          <p *ngIf="!contextual">{{ selectedScopeGroup?.label }}</p>
        </div>
        <div class="workflow-decision-editor__column-actions">
          <span *ngIf="!contextual" aria-live="polite">
            {{ visibleRuleRows.length }} rule{{
              visibleRuleRows.length === 1 ? "" : "s"
            }}
          </span>
          <button
            uiButton
            *ngIf="canAuthor && selectedScopeGroup?.authorable"
            type="button"
            class="workflow-decision-editor__header-action"
            data-testid="add-workflow-decision"
            [attr.aria-label]="
              statusChecksPresentation
                ? 'Add decision check'
                : contextual
                  ? 'Require a recorded decision'
                  : null
            "
            [disabled]="
              interactionBlocked ||
              notificationEditorPending ||
              referenceLoading ||
              !referenceDataReady
            "
            (click)="beginAddDecision()"
          >
            {{
              statusChecksPresentation
                ? "Add decision check"
                : contextual
                  ? "Require a decision"
                  : "Add rule"
            }}
          </button>
        </div>
      </header>

      <p *ngIf="!contextual" class="workflow-decision-editor__combination-note">
        Across workflow, step, specific status-change, and legacy rules, every
        different type/result/action/destination requirement must pass. Matching
        requirements are evaluated once; specific rules do not replace broader
        ones.
      </p>
      <p
        *ngIf="
          contextual && !statusCheckContext() && visibleRuleRows.length > 1
        "
        class="workflow-decision-editor__combination-note workflow-decision-editor__combination-note--compact"
      >
        Decision checks combine where their scopes apply.
      </p>

      <p
        *ngIf="visibleRuleRows.length === 0 && !statusCheckContext()"
        class="workflow-decision-editor__empty"
      >
        {{
          statusChecksPresentation
            ? "No decision checks are configured for this step."
            : "No recorded decisions are required here."
        }}
        <ng-container *ngIf="canAuthor && selectedScopeGroup?.authorable">
          {{
            statusChecksPresentation
              ? "Add a check when a status change depends on an existing decision result."
              : "Require a decision to check existing evidence before a status change can continue."
          }}
        </ng-container>
      </p>

      <ng-container
        *ngIf="
          statusChecksPresentation && statusCheckContext();
          else ordinaryDecisionNavigator
        "
      >
        <ng-container *ngFor="let section of decisionNavigatorSections">
          <section
            class="workflow-decision-editor__check-section"
            [attr.data-testid]="'status-checks-' + section.key"
          >
            <h6>
              {{ section.title }} <span>({{ section.rows.length }})</span>
            </h6>
            <p>{{ section.description }}</p>
            <p
              *ngIf="section.rows.length === 0"
              class="workflow-decision-editor__check-empty"
            >
              No decision checks saved in this scope.
            </p>
            <ng-container
              *ngIf="section.rows.length > 0"
              [ngTemplateOutlet]="decisionRules"
              [ngTemplateOutletContext]="{ $implicit: section.rows }"
            ></ng-container>
          </section>
        </ng-container>
        <details
          *ngIf="separateWorkflowCheckCount > 0"
          class="workflow-decision-editor__scope-disclosure"
        >
          <summary>
            Whole-workflow checks ({{ separateWorkflowCheckCount }})
          </summary>
          <p>
            Separate scope: these check workflow status changes, including
            workflow completion. They are not added to every step status change.
          </p>
          <button
            uiButton
            type="button"
            class="workflow-decision-editor__inline-action"
            [disabled]="interactionBlocked || notificationEditorPending"
            (click)="reviewWholeWorkflowChecks()"
          >
            Review whole-workflow checks
          </button>
        </details>
      </ng-container>
      <ng-template #ordinaryDecisionNavigator>
        <ng-container
          *ngIf="
            visibleRuleRows.length > 0 &&
            (!contextual ||
              visibleRuleRows.length > 1 ||
              (!selectedRow && !selectedLegacyRow))
          "
          [ngTemplateOutlet]="decisionRules"
          [ngTemplateOutletContext]="{ $implicit: visibleRuleRows }"
        ></ng-container>
      </ng-template>
      <ng-template #decisionRules let-rules>
        <div class="workflow-decision-editor__rule-list">
          <button
            [wrap]="true"
            uiButton
            [text]="true"
            [outlined]="false"
            *ngFor="let rule of rules; trackBy: trackNavigatorRow"
            type="button"
            class="workflow-decision-editor__rule-row"
            [class.workflow-decision-editor__rule-row--selected]="
              rule.key === selectedNavigatorKey
            "
            [class.workflow-decision-editor__rule-row--invalid]="
              rule.kind === 'structured' && rule.structured?.decision === null
            "
            [class.workflow-decision-editor__rule-row--legacy]="
              rule.kind === 'legacy'
            "
            [attr.aria-current]="
              rule.key === selectedNavigatorKey ? 'true' : null
            "
            [attr.aria-label]="navigatorAriaLabel(rule)"
            [disabled]="interactionBlocked || notificationEditorPending"
            (click)="reviewNavigatorRow(rule, $event.detail === 0)"
          >
            <ng-container *ngIf="contextual; else detailedDecisionRule">
              <span class="workflow-decision-editor__rule-topline">
                <strong>{{ navigatorRequirementLabel(rule) }}</strong>
                <span
                  *ngIf="
                    rule.kind === 'structured' &&
                    rule.structured?.decision === null
                  "
                  class="workflow-decision-editor__rule-alert"
                >
                  {{
                    inspectionMode ? "Incomplete saved entry" : "Needs repair"
                  }}
                </span>
              </span>
              <span>
                {{ navigatorContextLabel(rule) }}
                <ng-container
                  *ngIf="navigatorExceptionLabel(rule) as exception"
                >
                  · {{ exception }}
                </ng-container>
              </span>
            </ng-container>

            <ng-template #detailedDecisionRule>
              <span class="workflow-decision-editor__rule-topline">
                <span class="workflow-decision-editor__rule-type">
                  {{
                    rule.kind === "legacy"
                      ? "Legacy compatibility"
                      : rule.direct
                        ? "Every status change"
                        : "Specific status change"
                  }}
                </span>
                <span
                  *ngIf="
                    rule.kind === 'structured' &&
                    rule.structured?.decision === null
                  "
                  class="workflow-decision-editor__rule-alert"
                >
                  {{
                    inspectionMode ? "Incomplete saved entry" : "Needs repair"
                  }}
                </span>
              </span>
              <strong>{{ rule.locationLabel }}</strong>
              <ng-container *ngIf="rule.structured as row; else legacyRule">
                <span
                  >{{ decisionKindLabel(row) }} ·
                  {{ outcomeLabel(row.decision) }}</span
                >
                <span>{{ reactionLabel(row.decision) }}</span>
              </ng-container>
              <ng-template #legacyRule>
                <span *ngIf="rule.legacy as legacy">
                  {{ humanizeCode(legacy.decisionKind) }} · Read-only
                  compatibility requirement
                </span>
              </ng-template>
            </ng-template>
          </button>
        </div>
      </ng-template>
    </section>

    <section
      class="workflow-decision-editor__inspector"
      [hidden]="
        contextual && !selectedRow && !selectedLegacyRow && !isAddingDecision()
      "
      aria-labelledby="workflow-decision-form-title"
      data-testid="decision-inspector"
    >
      <header
        *ngIf="!contextualSummaryVisible && !(contextual && selectedLegacyRow)"
        class="workflow-decision-editor__column-header"
      >
        <div>
          <p *ngIf="!contextual" class="workflow-decision-editor__eyebrow">
            Inspector
          </p>
          <h5
            #decisionEditorHeading
            id="workflow-decision-form-title"
            tabindex="-1"
          >
            {{ inspectorTitle }}
          </h5>
          <p *ngIf="!contextual && inspectorLocationLabel">
            {{ inspectorLocationLabel }}
          </p>
        </div>
      </header>

      <div
        *ngIf="contextualSummaryVisible"
        class="workflow-decision-editor__summary-body"
        data-testid="decision-rule-summary"
      >
        <ng-container *ngIf="selectedRow as row">
          <h5
            #decisionEditorHeading
            id="workflow-decision-form-title"
            class="workflow-decision-editor__requirement-summary"
            tabindex="-1"
            aria-live="polite"
          >
            {{ ruleSummary }}
          </h5>
          <p
            *ngIf="singleRequirementContextLabel as contextLabel"
            class="workflow-decision-editor__requirement-context"
          >
            {{ contextLabel }}
          </p>

          <div
            *ngIf="row.decision === null"
            class="workflow-decision-editor__warning"
            role="status"
          >
            {{
              inspectionMode
                ? "This saved entry is incomplete and cannot be used. It remains visible as immutable evidence."
                : "This saved entry is incomplete and cannot be used. Repair it or remove it."
            }}
          </div>

          <ul
            *ngIf="selectedSavedCheckWarnings.length > 0"
            class="workflow-decision-editor__warning"
            data-testid="saved-check-warnings"
          >
            <li *ngFor="let warning of selectedSavedCheckWarnings">
              {{ warning }}
            </li>
          </ul>

          <dl
            *ngIf="row.decision"
            class="workflow-decision-editor__summary-grid"
          >
            <div>
              <dt>Intended roles</dt>
              <dd>{{ selectedRolesSummary }}</dd>
            </div>
            <div>
              <dt>Intended queue</dt>
              <dd>{{ selectedQueueSummary }}</dd>
            </div>
            <div
              class="workflow-decision-editor__notification-summary"
              *ngIf="
                row.decision?.notifications?.length ||
                row.decision?.notificationMessage
              "
            >
              <dt>Saved notifications</dt>
              <dd>
                <span>{{ selectedNotificationSummary }}</span>
                <span class="workflow-decision-editor__delivery-state"
                  >Configuration only · Delivery disconnected</span
                >
                <button
                  uiButton
                  [wrap]="true"
                  *ngIf="row.decision && (!contextual || row.stepNodeId)"
                  type="button"
                  class="workflow-decision-editor__inline-action"
                  data-testid="manage-decision-notifications"
                  [disabled]="interactionBlocked || notificationEditorPending"
                  (click)="openDecisionNotifications()"
                >
                  Inspect saved notifications
                </button>
              </dd>
            </div>
          </dl>

          <p
            *ngIf="row.decision && !statusChecksPresentation"
            class="workflow-decision-editor__intent-note"
          >
            Requires existing recorded evidence; adding this prerequisite does
            not create a staff action. Configure actions in Detail. Role and
            queue guidance does not grant access or assign work.
          </p>
          <p
            *ngIf="row.decision && statusChecksPresentation"
            class="workflow-decision-editor__intent-note"
          >
            This check uses a decision already on record. Role and queue
            guidance does not grant access or assign work.
          </p>
          <p
            *ngIf="unsupportedGraphReaction"
            class="workflow-decision-editor__warning"
            role="status"
          >
            Unsupported saved effect: graph workflows do not support
            status-changing reactions. Choose an allowed status change effect to
            repair it. Configure destinations in Outcomes &amp; routes.
          </p>

          <div
            class="workflow-decision-editor__summary-actions"
            role="group"
            [attr.aria-label]="
              statusChecksPresentation
                ? 'Selected decision check actions'
                : 'Selected decision rule actions'
            "
          >
            <button
              uiButton
              severity="primary"
              [outlined]="false"
              *ngIf="canAuthor"
              type="button"
              class="workflow-decision-editor__primary-action"
              data-testid="edit-decision-rule"
              [attr.aria-label]="
                row.decision === null
                  ? statusChecksPresentation
                    ? 'Repair incomplete decision check'
                    : 'Repair incomplete decision requirement'
                  : 'Edit ' +
                    decisionKindLabel(row).toLocaleLowerCase() +
                    (statusChecksPresentation ? ' check' : ' requirement')
              "
              [disabled]="
                interactionBlocked ||
                referenceLoading ||
                !referenceDataReady ||
                selectedLocationUnavailable
              "
              (click)="beginEditDecision()"
            >
              {{ row.decision === null ? "Repair" : "Edit" }}
            </button>
            <button
              uiButton
              *ngIf="canAuthor"
              type="button"
              [attr.aria-label]="
                'Remove ' +
                decisionKindLabel(row).toLocaleLowerCase() +
                (statusChecksPresentation ? ' check' : ' requirement')
              "
              [disabled]="actionButtons[2].disabled"
              (click)="onActionButtonClick(actionButtons[2])"
            >
              Remove
            </button>
          </div>

          <details
            class="workflow-decision-editor__technical"
            data-testid="decision-technical-codes"
          >
            <summary>Technical details</summary>
            <dl>
              <div>
                <dt>Scope</dt>
                <dd>
                  <code>{{ technicalScopeCode }}</code>
                </dd>
              </div>
              <div>
                <dt>Location</dt>
                <dd>
                  <code>{{ technicalLocationCode }}</code>
                </dd>
              </div>
              <div *ngIf="technicalStepNodeId">
                <dt>Node ID</dt>
                <dd>
                  <code>{{ technicalStepNodeId }}</code>
                </dd>
              </div>
              <div *ngIf="technicalTransitionIndex !== null">
                <dt>Transition index</dt>
                <dd>
                  <code>{{ technicalTransitionIndex }}</code>
                </dd>
              </div>
              <div>
                <dt>Decision index</dt>
                <dd>
                  <code>{{ row.decisionIndex }}</code>
                </dd>
              </div>
              <div *ngIf="statusChecksPresentation">
                <dt>Decision type</dt>
                <dd>
                  <code>{{ row.decision?.decisionKind || "null" }}</code>
                </dd>
              </div>
              <div *ngIf="statusChecksPresentation">
                <dt>Required result</dt>
                <dd>
                  <code>{{
                    row.decision?.requiredOutcomeCode ||
                      "null (Approved default)"
                  }}</code>
                </dd>
              </div>
              <div *ngIf="statusChecksPresentation">
                <dt>Effect</dt>
                <dd>
                  <code>{{
                    row.decision?.reaction?.reactionCode ||
                      "null (Permit default)"
                  }}</code>
                </dd>
              </div>
              <div *ngIf="statusChecksPresentation">
                <dt>Destination</dt>
                <dd>
                  <code>{{
                    row.decision?.reaction?.targetStateCode || "null"
                  }}</code>
                </dd>
              </div>
            </dl>
          </details>
        </ng-container>
      </div>

      <div
        *ngIf="decisionFormVisible"
        class="workflow-decision-editor__inspector-body"
        data-testid="decision-rule-form-panel"
      >
        <button
          uiButton
          *ngIf="contextual && !isAddingDecision()"
          type="button"
          class="workflow-decision-editor__back-action"
          (click)="returnToDecisionSummary()"
        >
          {{
            statusChecksPresentation
              ? "Back to check summary"
              : "Back to decision summary"
          }}
        </button>
        <p
          *ngIf="!contextual"
          class="workflow-decision-editor__rule-summary"
          aria-live="polite"
        >
          {{ ruleSummary }}
        </p>
        <p
          *ngIf="!statusChecksPresentation"
          class="workflow-decision-editor__hint"
        >
          Uses a recorded decision; it does not create one. Configure the staff
          action in Detail.
        </p>
        <p
          *ngIf="contextual"
          class="workflow-decision-editor__requirement-context"
        >
          {{ inspectorLocationLabel }}
        </p>
        <section
          *ngIf="contextual"
          class="workflow-decision-editor__preview"
          [attr.aria-label]="
            statusChecksPresentation
              ? 'Decision check preview'
              : 'Decision requirement preview'
          "
        >
          <strong>{{ ruleSummary }}</strong>
          <p
            *ngIf="
              decisionFormValues.decisionKind &&
              (!decisionFormValues.reactionCode ||
                decisionFormValues.reactionCode === 'permit')
            "
          >
            Missing evidence blocks the status change. Every applicable
            {{ statusChecksPresentation ? "check" : "requirement" }} must pass.
          </p>
        </section>
        <p
          *ngIf="unsupportedGraphReaction"
          class="workflow-decision-editor__warning"
          role="status"
        >
          This saved effect is unsupported in graph workflows. Choose an allowed
          status change effect to repair it. Configure destinations in Outcomes
          &amp; routes.
        </p>
        <p
          *ngIf="decisionFormValues.reactionCode === 'block'"
          class="workflow-decision-editor__warning"
          role="status"
        >
          This effect blocks every status change in the selected scope, even
          when the required decision result exists.
        </p>
        <p
          *ngIf="!contextual && selectedDecision()"
          class="workflow-decision-editor__hint"
        >
          {{
            inspectionMode
              ? "Where this rule applies is part of the immutable workflow definition."
              : "Where this rule applies is fixed after creation. Remove it and add another rule to move it."
          }}
        </p>
        <p *ngIf="!canAuthor" class="workflow-decision-editor__hint">
          {{
            inspectionMode
              ? "This rule belongs to an immutable workflow version."
              : "You can inspect this rule. Author permission is required to change it."
          }}
        </p>
        <p
          *ngIf="hasInertPersistedTarget()"
          class="workflow-decision-editor__warning"
          role="status"
        >
          {{
            inspectionMode
              ? "This saved destination status is ignored by the current reaction and remains visible as immutable evidence."
              : graphWorkflow
                ? "This saved destination status is ignored. Clear it explicitly before applying other changes."
                : "This saved destination status is ignored by the current reaction. Clear it explicitly or choose a status-changing reaction before applying other changes."
          }}
        </p>
        <div
          *ngIf="hasInvalidEligibleRoleEntries()"
          class="workflow-decision-editor__warning"
          role="status"
        >
          <p>
            {{
              inspectionMode
                ? "This rule contains an invalid empty eligible-role entry. It remains visible as immutable evidence."
                : "This rule contains an invalid empty eligible-role entry. Remove it explicitly before applying other changes."
            }}
          </p>
          <button
            uiButton
            [text]="true"
            [outlined]="false"
            *ngIf="!inspectionMode"
            type="button"
            class="workflow-decision-editor__text-button"
            [disabled]="
              !canAuthor ||
              interactionBlocked ||
              referenceLoading ||
              selectedLocationUnavailable
            "
            (click)="removeInvalidEligibleRoleEntries()"
          >
            Remove invalid role entry
          </button>
        </div>

        <ui-form
          #decisionForm
          instanceId="workflow-draft-decision-rule-form"
          [attr.aria-label]="
            statusChecksPresentation
              ? 'Decision check details'
              : 'Decision rule details'
          "
          [config]="decisionFormConfig"
          [(formValues)]="decisionFormValues"
          [disabled]="
            !canAuthor ||
            interactionBlocked ||
            referenceLoading ||
            !referenceDataReady ||
            selectedLocationUnavailable
          "
          (formValuesChange)="onFormChange($event)"
        ></ui-form>
        <p class="workflow-decision-editor__hint">
          Role and queue guidance is saved metadata. It does not grant access,
          assign work, or create a separate decision or quorum.
        </p>

        <div
          *ngIf="
            contextual &&
            selectedRow?.stepNodeId &&
            (selectedRow?.decision?.notifications?.length ||
              selectedRow?.decision?.notificationMessage)
          "
          class="workflow-decision-editor__notification-summary"
        >
          <span>Notifications: {{ selectedNotificationSummary }}</span>
          <span class="workflow-decision-editor__delivery-state"
            >Configuration only · Delivery disconnected</span
          >
          <button
            uiButton
            [wrap]="true"
            type="button"
            class="workflow-decision-editor__inline-action"
            [disabled]="interactionBlocked"
            (click)="openDecisionNotifications()"
          >
            Inspect saved notifications
          </button>
        </div>
        <p
          *ngIf="decisionNotificationsVisible"
          class="workflow-decision-editor__delivery-state"
        >
          Configuration only · Delivery disconnected
        </p>
        <phms-workflow-notification-rule-editor
          *ngIf="
            decisionNotificationsVisible && selectedRow?.decision as decision
          "
          [rules]="decision.notifications || null"
          [canAuthor]="false"
          [inspectionMode]="inspectionMode"
          [interactionBlocked]="interactionBlocked || formDirty()"
          [referenceLoading]="referenceLoading"
          [roleCodes]="roleCodes"
          [queueCodes]="queueCodes"
          [managerTargetCodes]="managerNotificationTargetCodes"
          [externalTargetCodes]="externalNotificationTargetCodes"
          [resetVersion]="resetVersion"
          [ownerKey]="selectedRow?.locationSignature || ''"
          instanceId="workflow-decision-notification-rule"
          triggerLabel="when this required decision result is recorded"
          (rulesChange)="applyDecisionNotifications($event)"
          (pendingChangesChange)="onNotificationPendingChangesChange($event)"
        ></phms-workflow-notification-rule-editor>

        <p
          *ngIf="formDirty()"
          class="workflow-decision-editor__pending"
          role="status"
        >
          <strong>Save draft</strong> includes these decision-rule edits. You
          can also apply them locally before saving.
        </p>

        <details
          *ngIf="!contextual"
          class="workflow-decision-editor__technical"
          data-testid="decision-technical-codes"
        >
          <summary>Technical codes</summary>
          <dl>
            <div>
              <dt>Scope</dt>
              <dd>
                <code>{{ technicalScopeCode }}</code>
              </dd>
            </div>
            <div>
              <dt>Location</dt>
              <dd>
                <code>{{ technicalLocationCode }}</code>
              </dd>
            </div>
            <div *ngIf="technicalStepNodeId">
              <dt>Node ID</dt>
              <dd>
                <code>{{ technicalStepNodeId }}</code>
              </dd>
            </div>
            <div *ngIf="technicalTransitionIndex !== null">
              <dt>Transition index</dt>
              <dd>
                <code>{{ technicalTransitionIndex }}</code>
              </dd>
            </div>
            <div *ngIf="selectedRow as row">
              <dt>Decision index</dt>
              <dd>
                <code>{{ row.decisionIndex }}</code>
              </dd>
            </div>
            <div>
              <dt>Decision type</dt>
              <dd>
                <code>{{ decisionFormValues.decisionKind || "null" }}</code>
              </dd>
            </div>
            <div>
              <dt>Required result</dt>
              <dd>
                <code>{{
                  decisionFormValues.requiredOutcomeCode || "null"
                }}</code>
              </dd>
            </div>
            <div>
              <dt>Next action</dt>
              <dd>
                <code>{{ decisionFormValues.reactionCode || "null" }}</code>
              </dd>
            </div>
            <div>
              <dt>Destination</dt>
              <dd>
                <code>{{ decisionFormValues.targetStateCode || "null" }}</code>
              </dd>
            </div>
          </dl>
        </details>
      </div>

      <div
        *ngIf="canAuthor && decisionFormVisible"
        class="workflow-decision-editor__editor-actions"
        role="group"
        [attr.aria-label]="
          statusChecksPresentation
            ? 'Decision check editor actions'
            : 'Decision rule editor actions'
        "
      >
        <button
          uiButton
          severity="primary"
          [outlined]="false"
          *ngIf="!actionButtons[0].hidden"
          type="button"
          class="workflow-decision-editor__primary-action"
          [disabled]="actionButtons[0].disabled"
          (click)="onActionButtonClick(actionButtons[0])"
        >
          {{
            isAddingDecision()
              ? statusChecksPresentation
                ? "Add decision check"
                : "Add decision rule"
              : selectedRow?.decision === null
                ? statusChecksPresentation
                  ? "Replace invalid check"
                  : "Replace invalid rule"
                : statusChecksPresentation
                  ? "Apply decision check"
                  : "Apply decision rule"
          }}
        </button>
        <button
          uiButton
          *ngIf="!actionButtons[1].hidden"
          type="button"
          [disabled]="actionButtons[1].disabled"
          (click)="onActionButtonClick(actionButtons[1])"
        >
          {{ isAddingDecision() || contextual ? "Cancel" : "Reset form" }}
        </button>
        <button
          uiButton
          *ngIf="!actionButtons[2].hidden"
          type="button"
          [disabled]="actionButtons[2].disabled"
          (click)="onActionButtonClick(actionButtons[2])"
        >
          {{
            statusChecksPresentation
              ? "Remove decision check"
              : "Remove decision rule"
          }}
        </button>
      </div>

      <div
        *ngIf="selectedLegacyRow as legacy"
        class="workflow-decision-editor__inspector-body"
      >
        <ng-container *ngIf="contextual; else detailedLegacyRequirement">
          <h5
            #decisionEditorHeading
            id="workflow-decision-form-title"
            class="workflow-decision-editor__requirement-summary"
            tabindex="-1"
          >
            {{ legacyRuleSummary }}
          </h5>
          <p
            *ngIf="singleRequirementContextLabel as contextLabel"
            class="workflow-decision-editor__requirement-context"
          >
            {{ contextLabel }}
          </p>
          <p>
            {{
              statusChecksPresentation
                ? "Older read-only decision check. It must pass with every other applicable check."
                : "Older read-only requirement. It must be met with every other requirement."
            }}
          </p>
        </ng-container>
        <ng-template #detailedLegacyRequirement>
          <p class="workflow-decision-editor__legacy-label">
            Read-only compatibility evidence
          </p>
          <p>
            This older kind-only requirement is preserved on the selected status
            change. It cannot be edited here, and it remains separate from
            structured decision rules.
          </p>
          <p>
            Its compatibility meaning is to require an existing
            <strong>{{ humanizeCode(legacy.decisionKind) }}</strong> decision
            with an <strong>Approved</strong> result, then allow the requested
            status change. It combines with every other applicable requirement.
          </p>
        </ng-template>
        <details
          class="workflow-decision-editor__technical"
          data-testid="decision-technical-codes"
        >
          <summary>Technical codes</summary>
          <dl>
            <div>
              <dt>Scope</dt>
              <dd>
                <code>{{ legacy.scopeCode }}</code>
              </dd>
            </div>
            <div>
              <dt>Location</dt>
              <dd>
                <code>{{ legacy.locationKey }}</code>
              </dd>
            </div>
            <div *ngIf="legacy.stepNodeId">
              <dt>Node ID</dt>
              <dd>
                <code>{{ legacy.stepNodeId }}</code>
              </dd>
            </div>
            <div>
              <dt>Transition index</dt>
              <dd>
                <code>{{ legacy.transitionIndex }}</code>
              </dd>
            </div>
            <div>
              <dt>Legacy index</dt>
              <dd>
                <code>{{ legacy.legacyIndex }}</code>
              </dd>
            </div>
            <div>
              <dt>Decision type</dt>
              <dd>
                <code>{{ legacy.decisionKind }}</code>
              </dd>
            </div>
          </dl>
        </details>
      </div>

      <div
        *ngIf="!selectedDecision() && !isAddingDecision() && !selectedLegacyRow"
        class="workflow-decision-editor__inspector-empty"
      >
        <p>
          {{
            statusChecksPresentation
              ? "Select a decision check to inspect or edit it."
              : inspectionMode
                ? "Select a rule to understand it."
                : "Select a rule to understand or edit it."
          }}
        </p>
      </div>
    </section>
  </div>
</section>
